import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';

/**
 * Media query that decides modal mode. `(min-width: 0px)` always matches, so
 * every drawer is a modal overlay at every viewport width.
 *
 * Stock Horizon used `(max-width: 989px)` here: above that the drawer opened
 * non-modal and `.page-wrapper` was given a `margin-right` so page content was
 * squeezed into the remaining space. On a 1440px viewport that reflowed the
 * whole page to 960px the moment an item was added to the cart. The drawer is
 * meant to sit on top of the page, not resize it, so modal mode is now
 * unconditional.
 *
 * Kept as a MediaQueryList rather than a plain boolean so the mode-switching
 * plumbing below stays intact — the query simply never changes state, so
 * `#onModalBreakpointChange` never fires.
 */
const MODAL_QUERY = '(min-width: 0px)';

/**
 * Class that parks the panel off-screen, backdrop transparent, between
 * `showModal()` and the start of the slide. See {@link ThemeDrawer#startSlide}.
 */
const ARMED_CLASS = 'theme-drawer__dialog--armed';

/**
 * How long the armed state may hold the slide back if the frame rate never
 * settles, in ms.
 *
 * Caps the delay a shopper can ever wait before the panel starts moving: the
 * point of arming is to move the stall to where it is invisible, not to trade it
 * for an open that never comes. Long enough to clear the bursts measured on the
 * cart drawer (88ms + 175ms, see `#startSlide`) on a slower device.
 */
const ARM_TIMEOUT = 300;

/** Frame interval, in ms, at or below which the main thread counts as settled. */
const SMOOTH_FRAME_MS = 24;

/** Consecutive settled frames required before the slide is allowed to start. */
const SMOOTH_FRAMES = 2;

/**
 * A drawer that opens from the right side.
 *
 * The drawer overlays the page with a backdrop at every viewport width. The
 * panel is a modal dialog (`showModal()`) — native focus trap, scroll-lock,
 * and ARIA semantics. Focus moves to the close button on open and returns to
 * the trigger on close; clicking the backdrop or pressing Escape closes it.
 *
 * Dispatches {@link DrawerOpenEvent} and {@link DrawerCloseEvent}.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel - The dialog that owns all visual styles and animation.
 *
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  /** Monotonically increasing counter for z-index stacking of multiple open drawers. */
  static #stackOrder = 0;

  /** @type {boolean} */
  #isClosing = false;

  /** When true, the drawer will reopen after the current close animation finishes. */
  /** @type {{ enter?: 'inline-start' } | false} */
  #deferredOpen = false;

  /** Element to restore focus to when the drawer closes. */
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {MediaQueryList} */
  #modalQuery = window.matchMedia(MODAL_QUERY);

  /**
   * @returns {boolean} Whether the drawer is currently open.
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#modalQuery.addEventListener('change', this.#onModalBreakpointChange);

    // Sync the static stack counter with any --drawer-stack-order set by the
    // synchronous restore script (which runs before this module loads).
    const restoredOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  /**
   * Restore path: the inline script in theme-drawer.liquid set [open] on
   * <theme-drawer> + <dialog> before this module loaded. The dialog is
   * already visible — we just wire close listeners. We deliberately skip
   * trapFocus and focus moves: the user is loading a fresh page and
   * expects focus on main content, not inside a drawer left over from
   * the previous session.
   */
  #onRestore() {
    const { panel } = this.refs;
    if (this.#modalQuery.matches) {
      lockScroll(panel);
    }

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#disarm();
    unlockScroll(this.refs.panel);
    this.#modalQuery.removeEventListener('change', this.#onModalBreakpointChange);
    this.#removeEventListeners();
    removeTrapFocus();
    this.#deferredOpen = false;
  }

  /**
   * Closes the drawer when the user clicks the backdrop (outside the dialog
   * content area).
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #onBackdropClick = (event) => {
    const { panel } = this.refs;

    if (isClickedOutside(event, panel)) {
      if (this.#hasOpenNestedDialog()) return;

      this.close();
    }
  };

  /**
   * @returns {boolean} Whether the drawer panel contains an open dialog other than itself.
   */
  #hasOpenNestedDialog() {
    return this.#getOpenNestedDialog() !== null;
  }

  /**
   * @returns {HTMLDialogElement | null} The open dialog nested inside the drawer panel, if any.
   */
  #getOpenNestedDialog() {
    return /** @type {HTMLDialogElement | null} */ (this.refs.panel.querySelector('dialog[open]'));
  }

  /**
   * Switches the dialog between modal and non-modal when the viewport
   * crosses the modal breakpoint while the drawer is open.
   *
   * Inert while {@link MODAL_QUERY} always matches — kept so restoring a
   * breakpoint-dependent mode is a one-line change to that query.
   */
  #onModalBreakpointChange = () => {
    if (!this.isOpen) return;

    const { panel } = this.refs;
    const nestedDialog = this.#getOpenNestedDialog();
    const nestedActiveElement =
      nestedDialog?.contains(document.activeElement) && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Close the current dialog mode and immediately reopen in the new mode.
    // No animation — the drawer stays visually in place.
    panel.close();
    removeTrapFocus();

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      unlockScroll(panel);
      panel.show();
      trapFocus(panel);
    }

    if (nestedDialog?.open) {
      this.#bringNestedDialogToFront(nestedDialog, nestedActiveElement);
    } else {
      this.#focusInitialElement();
    }

    // Ensure keydown and click listeners are registered. The sessionStorage
    // restore path bypasses open(), so these may not have been added yet.
    // addEventListener deduplicates identical listeners, so this is safe
    // even if they were already registered.
    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  };

  /**
   * Closes the drawer when the user presses Escape.
   * preventDefault() suppresses the native dialog cancel event in modal mode.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    // isComposing: don't close while an Input Method Editor (e.g. Japanese,
    // Chinese, Korean input) consumes Escape to dismiss its candidate list —
    // can happen inside the cart's discount-code field.
    // defaultPrevented: let nested components handle Escape first.
    if (event.isComposing || event.defaultPrevented) return;
    if (!this.isOpen) return;
    if (!this.#isTopmost()) return;

    event.preventDefault();
    this.close();
  };

  /**
   * Shows the drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction. `'inline-start'` slides left-to-right (used when the chat docks).
   */
  open({ enter } = {}) {
    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }
    if (this.#isClosing) {
      this.#deferredOpen = { enter };
      return;
    }

    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';

    // Arming holds the slide back until the panel's first render has been paid
    // for — see #startSlide. `inline-start` opts out: it starts the panel half a
    // width from open, i.e. already on screen, so there is no off-screen parking
    // spot that leads into it without a jump. That entry is the chat drawer's
    // dock, which carries none of the third-party embeds arming exists to
    // absorb. Reduced motion is NOT carved out — these keyframes run whatever
    // the setting, so a reduced-motion shopper sees the same teleport and needs
    // the same fix.
    const arm = enter !== 'inline-start';
    panel.classList.add(arm ? ARMED_CLASS : openClass);

    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      panel.show();
      trapFocus(panel);
    }

    this.#focusInitialElement();

    if (arm) {
      this.#startSlide(openClass);
    } else {
      onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });
    }

    this.setAttribute('open', '');
    this.dispatchEvent(new DrawerOpenEvent());

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  /**
   * Swaps the armed panel onto its slide-in animation, once the main thread has
   * gone quiet.
   *
   * A closed `<dialog>` is `display: none`, so its whole subtree is unrendered.
   * Making it rendered again is what provokes any embed inside it to rebuild —
   * and on the cart drawer that is Shopify's dynamic checkout block, whose
   * PayPal `zoid` iframe tears itself down and re-renders every time. Measured
   * on the preview theme at 1440x900: an 88ms task on the frame the panel
   * appears, then a 175ms one, straddling the whole 180ms slide. `animationstart`
   * and `animationend` for `drawer-slide-in` landed in the SAME millisecond and
   * the recorded x-positions went 1440 -> (206ms frozen) -> 960 — not one
   * intermediate frame. The panel teleported. Detaching the checkout block and
   * repeating the open gave 11 frames at a 17ms cadence and no long task at all,
   * which is what identified the cause.
   *
   * `translate` being composited does not save it: a composited animation still
   * has to be committed by the main thread before the compositor can run it, and
   * here the main thread never got a frame out during the animation's lifetime.
   *
   * So the panel is parked off-screen with a transparent backdrop first, the
   * rebuild burns itself out there where nothing is visible, and the slide
   * starts on the first idle moment after — against a quiet main thread, with
   * {@link ARM_TIMEOUT} as the backstop. The shopper waits about as long as
   * before for the panel to appear; the difference is that it now arrives by
   * sliding rather than by popping into place.
   *
   * What is waited for is the frame rate settling, not an idle callback.
   * `requestIdleCallback` was tried first and disarmed too early: the checkout
   * block renders a loading skeleton, goes idle, and only then swaps in the real
   * buttons, so the idle moment fell in the gap between the two bursts and the
   * slide was eaten by the second (74ms + 50ms tasks landing on it). Counting
   * {@link SMOOTH_FRAMES} consecutive frames no longer apart than
   * {@link SMOOTH_FRAME_MS} measures the precondition directly — the browser is
   * painting smoothly right now — rather than inferring it.
   *
   * @param {string} openClass - The animation class to swap the armed state for.
   */
  #startSlide(openClass) {
    const { panel } = this.refs;
    const deadline = performance.now() + ARM_TIMEOUT;
    let previous = performance.now();
    let settled = 0;

    const check = () => {
      // A close, or a second open, may have landed while we waited. #disarm
      // drops the class, which is what ends this loop.
      if (!panel.classList.contains(ARMED_CLASS)) return;

      const now = performance.now();
      settled = now - previous <= SMOOTH_FRAME_MS ? settled + 1 : 0;
      previous = now;

      if (settled < SMOOTH_FRAMES && now < deadline) {
        requestAnimationFrame(check);
        return;
      }

      panel.classList.replace(ARMED_CLASS, openClass);
      onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });
    };

    requestAnimationFrame(check);
  }

  /**
   * Drops the armed state, which also ends the pending {@link ThemeDrawer#startSlide} loop.
   *
   * @returns {boolean} Whether the panel was still armed — i.e. had yet to
   *   become visible, so a close needs no slide-out to undo.
   */
  #disarm() {
    const { panel } = this.refs;
    const wasArmed = panel.classList.contains(ARMED_CLASS);
    panel.classList.remove(ARMED_CLASS);

    return wasArmed;
  }

  /**
   * Bumps this drawer's z-index to the top of the stack and replays
   * the slide-in animation. Called when open() is invoked on an
   * already-open drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction.
   */
  #bringToFront({ enter } = {}) {
    const { panel } = this.refs;

    // Re-opening an armed drawer means the slide it was waiting for is the one
    // being replayed here — drop the armed state so both do not run at once.
    this.#disarm();

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    // In modal mode, dialogs live in the browser's top layer where z-index
    // is ignored — stacking follows showModal() call order. Re-calling
    // showModal() moves this dialog to the top of the stack.
    if (this.#modalQuery.matches && panel.open) {
      lockScroll(panel);
      panel.close();
      panel.showModal();
    }

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);
    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });

    // Re-fire so persistDrawerState picks up the new stack order.
    this.dispatchEvent(new DrawerOpenEvent());
  }

  #removeEventListeners() {
    const { panel } = this.refs;

    document.removeEventListener('keydown', this.#onKeyDown);
    panel.removeEventListener('click', this.#onBackdropClick);
  }

  /**
   * Moves focus to the close button inside the drawer panel.
   * Every theme-drawer renders the close button via theme-drawer-header.
   * If a future drawer omits it, that drawer is responsible for its own focus management.
   */
  #focusInitialElement() {
    const closeButton = /** @type {HTMLElement | null} */ (
      this.refs.panel.querySelector('.theme-drawer__close-button')
    );
    closeButton?.focus();
  }

  /**
   * Reopens a nested modal after the drawer panel changes mode so it remains
   * above the drawer in the browser top layer.
   *
   * @param {HTMLDialogElement} dialog - The nested dialog to restack.
   * @param {HTMLElement | null} focusTarget - Element to refocus inside the nested dialog.
   */
  #bringNestedDialogToFront(dialog, focusTarget) {
    dialog.close();
    dialog.showModal();

    if (focusTarget && document.contains(focusTarget) && dialog.contains(focusTarget)) {
      focusTarget.focus();
    }
  }

  /**
   * Slides the drawer out, waits for the animation, then closes the dialog.
   */
  async #closeDialog() {
    if (!this.isOpen) return;
    if (this.#isClosing) return;
    this.#isClosing = true;

    this.#removeEventListeners();
    removeTrapFocus();

    const { panel } = this.refs;

    this.removeAttribute('open');
    this.dispatchEvent(new DrawerCloseEvent());

    unlockScroll(panel);

    // A panel closed while still armed was never on screen, so sliding it out
    // would mean flashing it into view first. Skip straight to the close.
    const wasArmed = this.#disarm();

    if (panel.open && !wasArmed) {
      // Cancel any in-progress open animation before starting the close.
      panel.classList.remove('theme-drawer__dialog--opening', 'theme-drawer__dialog--opening-inline-start');

      panel.classList.add('theme-drawer__dialog--closing');
      await onAnimationEnd(panel, undefined, { subtree: false });
      panel.classList.remove('theme-drawer__dialog--closing');
    }

    panel.close();
    this.style.removeProperty('--drawer-stack-order');

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
    }

    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;
    // A Section Rendering API morph between open and close can replace the
    // trigger node. The JS reference stays valid but the element is detached
    // from the live DOM — and .focus() on a detached node is a silent no-op,
    // so we explicitly check and fall back to a fresh query.
    if (trigger && document.contains(trigger)) {
      trigger.focus();
    } else {
      /** @type {HTMLElement | null} */ (document.querySelector(`[aria-controls="${this.id}"]`))?.focus();
    }

    this.#isClosing = false;

    // Reconcile: if open() was called during the close animation, reopen now.
    if (this.#deferredOpen) {
      const options = this.#deferredOpen;
      this.#deferredOpen = false;
      this.open(options);
    }
  }

  /**
   * Closes the drawer.
   */
  async close() {
    if (this.#isClosing) {
      // A close is already in progress — cancel any deferred open so the
      // drawer stays closed when the animation finishes.
      this.#deferredOpen = false;
      return;
    }
    await this.#closeDialog();
  }

  /**
   * Toggles the drawer. If the drawer is open but not the topmost in the
   * stack, it is brought to front instead of closed.
   */
  toggle() {
    if (this.isOpen && this.#isTopmost()) {
      return this.close();
    }
    return this.open();
  }

  /**
   * @returns {boolean} Whether this drawer has the highest stack order among all open drawers.
   */
  #isTopmost() {
    const myOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    for (const other of document.querySelectorAll('theme-drawer[open]')) {
      if (other === this) continue;
      const otherOrder = Number(/** @type {HTMLElement} */ (other).style.getPropertyValue('--drawer-stack-order') || 0);
      if (otherOrder > myOrder) return false;
    }
    return true;
  }
}

if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
}

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(DrawerOpenEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:close';
}
